import React, { useEffect, useMemo } from "react";
import { useLang } from "./i18n";
import { IntlProvider, createIntl, createIntlCache } from "react-intl";

import enMessages from "./messages/en";
import esMessages from "./messages/es";
import caMessages from "./messages/ca";
import { getConfig } from "./i18n";

const flattenMessages = (nestedMessages, prefix = "") => {
	if (nestedMessages == null) return {};

	return Object.keys(nestedMessages).reduce((messages, key) => {
		const value = nestedMessages[key];
		const prefixedKey = prefix ? `${prefix}.${key}` : key;

		if (typeof value === "string") Object.assign(messages, { [prefixedKey]: value });
		else Object.assign(messages, flattenMessages(value, prefixedKey));

		return messages;
	}, {});
};

const allMessages = {
	en: enMessages,
	es: esMessages,
	ca: caMessages,
};

const flatByLocale = {
	en: flattenMessages(enMessages),
	es: flattenMessages(esMessages),
	ca: flattenMessages(caMessages),
};

const cache = createIntlCache();

function buildIntl(locale) {
	const lang = locale === "en" ? "en" : locale === "ca" ? "ca" : "es";
	return createIntl(
		{ locale: lang, messages: flatByLocale[lang] || flatByLocale.es },
		cache
	);
}

/** Intl mutable para helpers fuera de React (logger, api…). Se sincroniza al cambiar idioma. */
export let intl = buildIntl(getConfig()?.selectedLang || "es");

export function syncStandaloneIntl(locale) {
	intl = buildIntl(locale);
}

export function I18nProvider({ children }) {
	const locale = useLang();
	const messages = useMemo(() => flatByLocale[locale] || flatByLocale.es, [locale]);

	useEffect(() => {
		syncStandaloneIntl(locale);
	}, [locale]);

	return (
		<IntlProvider locale={locale} messages={messages}>
			{children}
		</IntlProvider>
	);
}
